/**
 * Yap length, in one place: the composer, the tribe composer, the edit modal
 * and the service-side checks all read it from here.
 *
 * Legion has no limit. Nothing in firestore.rules caps post length (for anyone),
 * so these are client checks.
 */
export const YAP_MAX_CHARACTERS = 1000;

/** The character limit for this author, or null for no limit (Legion). */
export function yapCharLimit(author: { rank?: string | null } | null | undefined): number | null {
  return String(author?.rank || '').toLowerCase() === 'legion' ? null : YAP_MAX_CHARACTERS;
}

/** True when `text` is over the author's limit. */
export function exceedsYapLimit(text: string, author: { rank?: string | null } | null | undefined): boolean {
  const limit = yapCharLimit(author);
  return limit !== null && text.length > limit;
}

/**
 * Feeds show long yaps collapsed, with "See more". A yap is collapsed when it
 * runs past PREVIEW_MAX_CHARS characters or PREVIEW_MAX_LINES lines.
 */
export const PREVIEW_MAX_CHARS = 300;
export const PREVIEW_MAX_LINES = 6;

/**
 * The collapsed form of a long yap. Cuts at the last whitespace before the
 * limit, so a word, @mention or link is never split (a mention or URL cut in
 * half would render as a different, wrong one).
 */
export function previewText(
  text: string,
  maxChars: number = PREVIEW_MAX_CHARS,
  maxLines: number = PREVIEW_MAX_LINES,
): { preview: string; truncated: boolean } {
  const full = text || '';
  let cut = full;
  const lines = cut.split('\n');
  if (lines.length > maxLines) cut = lines.slice(0, maxLines).join('\n');
  if (cut.length > maxChars) {
    const head = cut.slice(0, maxChars);
    const lastSpace = Math.max(head.lastIndexOf(' '), head.lastIndexOf('\n'));
    // A single unbroken token longer than most of the limit: cut it anyway.
    cut = lastSpace > maxChars * 0.5 ? head.slice(0, lastSpace) : head;
  }
  if (cut.length >= full.length) return { preview: full, truncated: false };
  return { preview: cut.replace(/\s+$/, '') + '…', truncated: true };
}
